import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, FloppyDisk } from '@phosphor-icons/react';

function SaveTemplateModal({ subtasks, taskId, onClose, onSaved }) {
  const API = `${window.location.origin}/api`;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');

    try {
      const res = await fetch(`${API}/tasks/${taskId}/save-as-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), description: description.trim() || undefined }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      const template = await res.json();
      if (onSaved) onSaved(template);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-md w-full"
        data-testid="save-template-modal"
      >
        <div className="border-b border-slate-200 p-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Save as Template</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-save-template">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CAC Annual Return Checklist"
              required
              autoFocus
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="template-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this checklist"
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="template-description-input"
            />
          </div>

          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs font-medium text-slate-500 mb-2">{subtasks.length} items will be saved:</p>
            <ul className="space-y-1">
              {subtasks.map((st, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />
                  {st.title}
                </li>
              ))}
            </ul>
          </div>

          {error && <p className="text-xs text-red-600" data-testid="save-template-error">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-4 py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              data-testid="save-template-submit"
            >
              <FloppyDisk size={16} weight="fill" />
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2.5 rounded-lg text-sm"
              data-testid="save-template-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default SaveTemplateModal;
